import { Badge } from "@hub/components/ui/badge";
import { Button } from "@hub/components/ui/button";
import { Input } from "@hub/components/ui/input";
import { Select, SelectContent, SelectTrigger, SelectValue } from "@hub/components/ui/select";
import { engineHostLabel } from "@hub/lib/engine-host";
import { entraCallbackUri } from "@hub/lib/entra-callback";
import { InstanceHistory } from "@hub/ui/InstanceHistory";
import { type ProvisioningInstance, ProvisioningProgress } from "@hub/ui/ProvisioningProgress";
import { ReleaseItems } from "@hub/ui/ReleaseItems";
import { useReleases } from "@hub/ui/use-releases";
import { useEffect, useState } from "react";
import { ApiError, apiFetch } from "./api";
import { relativeTime } from "./format";
import { pinLabel } from "./org-form";

/** Extends ProvisioningInstance (rather than the other way around) because
 *  ProvisioningProgress's own module is the one place that needs to say,
 *  precisely, which fields a live phase card cannot render without. */
interface InstanceDetail extends ProvisioningInstance {
  host: string;
  ownerName: string | null;
  ownerEmail: string | null;
  desiredVersion: string | null;
  appVersion: string | null;
  runtimeVersion: string | null;
  runtimeOk: boolean | null;
  lastSeenAt: string | null;
  stale: boolean;
  createdAt: string;
  decommissionedAt: string | null;
  /** Non-null means Graph refused to register this host's Microsoft callback
   *  as NOT PERMITTED (Entra admin consent for the hub's own app
   *  registration is not granted yet) -- see the schema's own doc on this
   *  column. Deliberately checked independent of `status`: the box can reach
   *  `running` with this still set, and that is exactly when an operator
   *  most needs to see it, not less. */
  entraRedirectPendingAt: string | null;
  convergeStatus: string | null;
  orgSlug: string;
  orgName: string;
  orgChannel: string;
  orgPinnedVersion: string | null;
  pinnedVersion: string | null;
  envVersion: string | null;
  orgConfigVersion: string | null;
  envCurrent: boolean | null;
  configCurrent: boolean | null;
  engineHost: string | null;
}

/** One labelled figure in the identity strip, in the same card language as the
 *  fleet summary so the two pages read as one console. */
function Fact({
  label,
  value,
  badge,
  href,
}: {
  label: string;
  value: string;
  badge?: React.ReactNode;
  href?: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-card px-4 py-3">
      <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1 flex flex-wrap items-center gap-2 text-sm font-medium">
        {href ? (
          <a href={href} className="hover:underline">
            {value}
          </a>
        ) : (
          value
        )}
        {badge}
      </dd>
    </div>
  );
}

/** Per-instance detail: identity, the desired-version editor, and the last
 *  convergence outcome the box reported. */
/**
 * Recommission is the undo for a misclicked Decommission. A `terminated` row
 * was destroyed -- there is nothing left to bring back, and the server
 * refuses it (hub-2) -- so the console does not offer it.
 */
/**
 * Why the desired version cannot be changed here, or null when it can. A
 * pinned box runs its pin; the hub refuses a one-off move off it (409), so
 * the console does not offer one.
 */
export function desiredVersionLockedReason(pinnedVersion: string | null): string | null {
  return pinnedVersion ? `This Helm is pinned to ${pinnedVersion}. Unpin it first.` : null;
}

export function canRecommission(status: string): boolean {
  return status !== "terminated";
}

export function InstanceDetailPage({ id }: { id: string }) {
  const [detail, setDetail] = useState<{ instance: InstanceDetail } | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [desiredVersion, setDesiredVersion] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const { releases, releasesNote } = useReleases();
  const [pendingAction, setPendingAction] = useState<"decommission" | "recommission" | null>(null);
  const [actionSaving, setActionSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [destroySlugInput, setDestroySlugInput] = useState("");
  const [destroying, setDestroying] = useState(false);
  const [destroyError, setDestroyError] = useState<string | null>(null);
  // I3: AddInstancePage carries a failed kick-off /provision POST here via
  // the query string rather than only console.error'ing it -- the row
  // exists at `pending` either way, and the Provision action below
  // (ProvisioningProgress) is what actually recovers it.
  const [provisionKickoffFailed] = useState(
    () => new URLSearchParams(window.location.search).get("provisionKickoffFailed") === "1",
  );
  const [pinChoice, setPinChoice] = useState("");
  const [pinSaving, setPinSaving] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);
  const [entraCopied, setEntraCopied] = useState(false);
  // Clearing this flag in error is not self-healing (see the route's own
  // doc on handleAcknowledgeEntraCallback) -- it permanently removes the
  // only surfaced signal that this owner's Microsoft sign-in will fail, so
  // the button requires this explicit confirm step before it fires.
  const [confirmingEntraAck, setConfirmingEntraAck] = useState(false);
  const [entraAcking, setEntraAcking] = useState(false);
  const [entraAckError, setEntraAckError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<{ instance: InstanceDetail }>(`/api/instances/${id}`)
      .then((body) => {
        setDetail(body);
        setDesiredVersion(body.instance.desiredVersion ?? "");
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setNotFound(true);
          return;
        }
        setError(err instanceof Error ? err.message : "failed to load instance");
      });
  }, [id]);

  async function onSaveDesiredVersion() {
    setSaving(true);
    setSaveError(null);
    try {
      const body = await apiFetch<{ data: InstanceDetail }>(`/api/instances/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ desiredVersion }),
      });
      setDetail((prev) =>
        prev ? { ...prev, instance: { ...prev.instance, ...body.data } } : prev,
      );
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : "failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function setPin(pinnedVersion: string | null) {
    setPinSaving(true);
    setPinError(null);
    try {
      await apiFetch(`/api/instances/${id}/pin`, {
        method: "PATCH",
        body: JSON.stringify({ pinnedVersion }),
      });
      // The pin can also move desired_version (and an unpin can move it back
      // to the channel's), so take the server's whole row rather than patching.
      const fresh = await apiFetch<{ instance: InstanceDetail }>(`/api/instances/${id}`);
      setDetail(fresh);
      setDesiredVersion(fresh.instance.desiredVersion ?? "");
      setPinChoice("");
    } catch (err) {
      setPinError(err instanceof ApiError ? err.message : "failed to change the pin");
    } finally {
      setPinSaving(false);
    }
  }

  async function runAction(action: "decommission" | "recommission") {
    setActionSaving(true);
    setActionError(null);
    try {
      const body = await apiFetch<{ data: InstanceDetail }>(`/api/instances/${id}/${action}`, {
        method: "POST",
      });
      setDetail((prev) =>
        prev ? { ...prev, instance: { ...prev.instance, ...body.data } } : prev,
      );
      setPendingAction(null);
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : `failed to ${action} the instance`);
    } finally {
      setActionSaving(false);
    }
  }

  /** Passed to ProvisioningProgress as `onUpdate` — every poll tick, retry,
   *  and password acknowledgement lands here rather than the card holding a
   *  second, competing copy of the instance. */
  function updateInstance(patch: ProvisioningInstance) {
    setDetail((prev) => (prev ? { ...prev, instance: { ...prev.instance, ...patch } } : prev));
  }

  /**
   * Typing the slug back IS the confirmation (see @hub/routes/provisioning's
   * own doc) — this button stays disabled until it matches exactly, and the
   * server re-checks the same thing itself rather than trusting the client.
   * A destroy is not polled for afterward (see ProvisioningProgress's own
   * POLLING_STATUSES comment): re-fetch once to show the freshly claimed
   * `terminating` row, then leave the operator to refresh for the outcome.
   */
  async function runDestroy() {
    setDestroying(true);
    setDestroyError(null);
    try {
      await apiFetch(`/api/instances/${id}/destroy`, {
        method: "POST",
        body: JSON.stringify({ confirmSlug: destroySlugInput }),
      });
      const fresh = await apiFetch<{ instance: InstanceDetail }>(`/api/instances/${id}`);
      setDetail(fresh);
      setDestroySlugInput("");
    } catch (err) {
      setDestroyError(err instanceof ApiError ? err.message : "failed to destroy");
    } finally {
      setDestroying(false);
    }
  }

  async function copyEntraCallbackUri(uri: string) {
    try {
      await navigator.clipboard.writeText(uri);
      setEntraCopied(true);
      setTimeout(() => setEntraCopied(false), 1500);
    } catch {
      // Clipboard access can be denied by the browser; the URI is still
      // plain, selectable text right next to the button.
    }
  }

  /** Only clears the flag -- it does not re-attempt the Graph write. The
   *  operator has just done that step by hand in the Entra portal; the next
   *  registration this hub attempts on its own (a fresh instance, or a
   *  retried phase) will succeed or fail independently, live against
   *  whatever Graph says at that time. */
  async function acknowledgeEntraCallback() {
    setEntraAcking(true);
    setEntraAckError(null);
    try {
      await apiFetch(`/api/instances/${id}/acknowledge-entra-callback`, { method: "POST" });
      setDetail((prev) =>
        prev ? { ...prev, instance: { ...prev.instance, entraRedirectPendingAt: null } } : prev,
      );
      setConfirmingEntraAck(false);
    } catch (err) {
      setEntraAckError(err instanceof ApiError ? err.message : "failed to acknowledge");
    } finally {
      setEntraAcking(false);
    }
  }

  if (notFound) {
    return (
      <div className="w-full">
        <p className="text-sm text-muted-foreground">No such instance.</p>
        <a href="/" className="text-sm hover:underline">
          Back to the Helm Hub
        </a>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full">
        <p className="text-sm text-destructive">{error}</p>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="w-full">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  const { instance } = detail;
  const lockedReason = desiredVersionLockedReason(instance.pinnedVersion);

  return (
    <div className="w-full">
      <a href="/" className="text-sm text-muted-foreground hover:underline">
        ← Helm Hub
      </a>
      <h1 className="mt-2 mb-1 font-display text-2xl font-semibold tracking-tight">
        {instance.ownerName ?? instance.slug}
      </h1>
      <p className="mb-6 text-sm text-muted-foreground">
        <a
          href={`https://${instance.host}`}
          target="_blank"
          rel="noreferrer"
          className="hover:underline"
        >
          {instance.host}
        </a>
        {instance.ownerEmail ? ` · ${instance.ownerEmail}` : null}
      </p>

      <dl className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
        <Fact label="Running version" value={instance.appVersion ?? "—"} />
        <Fact label="Runtime version" value={instance.runtimeVersion ?? "unknown"} />
        <Fact label="Full Sail" value={engineHostLabel(instance.engineHost)} />
        <Fact
          label="Last seen"
          value={relativeTime(instance.lastSeenAt)}
          badge={instance.stale ? <Badge variant="destructive">stale</Badge> : null}
        />
        <Fact
          label="Organization"
          value={instance.orgName}
          href={`/orgs/${encodeURIComponent(instance.orgSlug)}`}
        />
        <Fact label="Release" value={pinLabel(instance)} />
        <Fact
          label="Hub env"
          value={
            instance.envCurrent === null ? "unknown" : instance.envCurrent ? "current" : "stale"
          }
          badge={
            instance.envCurrent === false ? (
              <Badge variant="destructive">re-render pending</Badge>
            ) : null
          }
        />
        <Fact
          label="Org config"
          value={
            instance.configCurrent === null
              ? "unknown"
              : instance.configCurrent
                ? "current"
                : "stale"
          }
        />
        <Fact label="Registered" value={new Date(instance.createdAt).toLocaleDateString()} />
      </dl>

      {provisionKickoffFailed ? (
        <div className="mb-6 rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          The instance was created, but the request to start provisioning it failed. Click Provision
          below to try again.
        </div>
      ) : null}

      {/* Independent of `instance.status` on purpose: this box can reach
          `running` with its Microsoft callback still unregistered, and that
          is exactly when an operator most needs to see this, not less. */}
      {instance.entraRedirectPendingAt ? (
        <div className="mb-6 space-y-3 rounded-xl border border-[var(--warning)]/40 bg-[var(--warning-bg)] p-5">
          <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-[var(--warning)]">
            Microsoft callback needs to be added by hand
          </h2>
          <p className="text-xs text-muted-foreground">
            The hub does not yet have Entra admin consent to register this automatically. Add the
            URI below to the Entra app registration's Authentication → Redirect URIs, then
            acknowledge it here. Once consent is granted, future registrations happen on their own —
            nothing else to do here after that.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <code className="select-all break-all rounded-md border border-border bg-background px-3 py-1.5 font-mono text-sm">
              {entraCallbackUri(instance.host)}
            </code>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => copyEntraCallbackUri(entraCallbackUri(instance.host))}
            >
              {entraCopied ? "Copied" : "Copy"}
            </Button>
            {confirmingEntraAck ? (
              <>
                <span className="text-xs text-muted-foreground">
                  Confirm the URI above is saved in Entra?
                </span>
                <Button
                  type="button"
                  size="sm"
                  onClick={acknowledgeEntraCallback}
                  disabled={entraAcking}
                >
                  {entraAcking ? "Saving…" : "Confirm"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmingEntraAck(false)}
                  disabled={entraAcking}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button type="button" size="sm" onClick={() => setConfirmingEntraAck(true)}>
                I have added this
              </Button>
            )}
          </div>
          {entraAckError ? <p className="text-xs text-destructive">{entraAckError}</p> : null}
        </div>
      ) : null}

      {/* Hub-driven provisioning's own live view: the phase tracker, the
          one-time break-glass password, and Retry on a failed walk. Not
          rendered once the box is running -- from there on the convergence
          block and history below are the whole story. */}
      {instance.status !== "running" ? (
        <ProvisioningProgress instance={instance} onUpdate={updateInstance} />
      ) : null}

      {/* The box-side converger writes its last outcome to a file; the agent
          reports it here. Without this an operator learns a box is stuck by
          SSHing to it, which a second person will not do. */}
      {instance.convergeStatus ? (
        <div
          className={`mb-6 rounded-xl border px-4 py-3 text-sm ${
            instance.convergeStatus.startsWith("error")
              ? "border-destructive/40 bg-destructive/10 text-destructive"
              : "border-border bg-card text-muted-foreground"
          }`}
        >
          <span className="font-medium">Last convergence: </span>
          {instance.convergeStatus}
        </div>
      ) : null}

      <InstanceHistory id={id} />

      <div className="mb-8 rounded-xl border border-border bg-card p-5">
        <h2 className="mb-3 font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Desired version
        </h2>
        <div className="flex flex-wrap items-center gap-3">
          <Select
            value={desiredVersion}
            onValueChange={(v) => v && setDesiredVersion(v)}
            disabled={lockedReason !== null}
          >
            <SelectTrigger className="w-72">
              <SelectValue placeholder="Pick a version" />
            </SelectTrigger>
            <SelectContent>
              <ReleaseItems releases={releases} />
            </SelectContent>
          </Select>
          <Button
            onClick={onSaveDesiredVersion}
            disabled={saving || lockedReason !== null || !desiredVersion.trim()}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
        {lockedReason ? <p className="mt-2 text-sm text-muted-foreground">{lockedReason}</p> : null}
        {saveError ? <p className="mt-2 text-sm text-destructive">{saveError}</p> : null}
        {releasesNote ? <p className="mt-2 text-xs text-muted-foreground">{releasesNote}</p> : null}
        <p className="mt-2 text-xs text-muted-foreground">
          Setting this records the choice. Nothing acts on it until the agent converges — this box
          keeps running {instance.appVersion ?? "its current version"} until then.
        </p>
      </div>

      <div className="mb-8 rounded-xl border border-border bg-card p-5">
        <h2 className="mb-3 font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Pin
        </h2>
        {instance.pinnedVersion ? (
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant="warning">Pinned to {instance.pinnedVersion}</Badge>
            <Button variant="outline" onClick={() => setPin(null)} disabled={pinSaving}>
              {pinSaving ? "Unpinning…" : "Unpin"}
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <Select value={pinChoice} onValueChange={(v) => v && setPinChoice(v)}>
              <SelectTrigger className="w-72" aria-label="Version to pin this box to">
                <SelectValue placeholder="Pick a version to pin" />
              </SelectTrigger>
              <SelectContent>
                <ReleaseItems releases={releases} />
              </SelectContent>
            </Select>
            <Button onClick={() => setPin(pinChoice)} disabled={pinSaving || !pinChoice}>
              {pinSaving ? "Pinning…" : "Pin"}
            </Button>
          </div>
        )}
        {pinError ? <p className="mt-2 text-sm text-destructive">{pinError}</p> : null}
        <p className="mt-2 text-xs text-muted-foreground">
          A pinned box ignores channel moves, and its desired version cannot be changed, until
          unpinned. Saving a desired version above on an unpinned box is a one-off move the next
          channel move overwrites.
        </p>
      </div>

      <div className="mb-8 rounded-xl border border-border bg-card p-5">
        <h2 className="mb-3 font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Status
        </h2>
        {instance.decommissionedAt ? (
          <div>
            <Badge variant="destructive" className="mb-2">
              Decommissioned
            </Badge>
            <p className="mb-3 text-xs text-muted-foreground">
              This instance no longer receives OAuth callbacks and its heartbeats are refused.
            </p>
            {!canRecommission(instance.status) ? (
              <p className="text-xs text-muted-foreground">
                This instance was destroyed and cannot be recommissioned.
              </p>
            ) : pendingAction === "recommission" ? (
              <div className="flex items-center gap-3">
                <span className="text-sm">Recommission this instance?</span>
                <Button onClick={() => runAction("recommission")} disabled={actionSaving}>
                  {actionSaving ? "Recommissioning…" : "Confirm"}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setPendingAction(null)}
                  disabled={actionSaving}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <Button variant="outline" onClick={() => setPendingAction("recommission")}>
                Recommission
              </Button>
            )}
          </div>
        ) : pendingAction === "decommission" ? (
          <div className="flex items-center gap-3">
            <span className="text-sm">
              Decommission this instance? It will stop receiving OAuth callbacks and its heartbeats
              will be refused immediately.
            </span>
            <Button
              variant="destructive"
              onClick={() => runAction("decommission")}
              disabled={actionSaving}
            >
              {actionSaving ? "Decommissioning…" : "Confirm"}
            </Button>
            <Button variant="ghost" onClick={() => setPendingAction(null)} disabled={actionSaving}>
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            variant="outline"
            className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
            onClick={() => setPendingAction("decommission")}
          >
            Decommission
          </Button>
        )}
        {actionError ? <p className="mt-2 text-sm text-destructive">{actionError}</p> : null}
      </div>

      <div className="mb-8 rounded-xl border border-destructive/40 bg-card p-5">
        <h2 className="mb-3 font-display text-sm font-semibold uppercase tracking-wider text-destructive">
          Destroy
        </h2>
        <p className="mb-3 text-xs text-muted-foreground">
          Terminates the EC2 instance, snapshots its volume first, and releases its DNS record.
          There is no undo — type the slug <code>{instance.slug}</code> to confirm.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Input
            className="max-w-xs"
            value={destroySlugInput}
            onChange={(e) => setDestroySlugInput(e.target.value)}
            placeholder={instance.slug}
            aria-label="Type the instance slug to confirm destroy"
          />
          <Button
            variant="destructive"
            disabled={destroying || destroySlugInput !== instance.slug}
            onClick={runDestroy}
          >
            {destroying ? "Destroying…" : "Destroy"}
          </Button>
        </div>
        {destroyError ? <p className="mt-2 text-sm text-destructive">{destroyError}</p> : null}
      </div>
    </div>
  );
}
